import { Component } from "react";

/**
 * 2026-02 (iter122) — App-weiter ErrorBoundary.
 *
 * Verhindert, dass ein unbehandelter Render-Fehler in einer Seite die
 * gesamte App weißmacht. Fängt Fehler an einem hohen Level ab und zeigt
 * eine wiederherstellbare Fehleranzeige mit „Neu laden" + „Zur Startseite"
 * an. Der komplette Fehler + Component-Stack wird in `console.error`
 * geloggt, sodass Support-/Deployer-Agents ihn abrufen können.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Ausführliches Logging: Fehler-Meldung + Stacktrace + React-Component-Stack.
    // Wird von Playwright-Testagenten als pageerror/console.error abgefangen.
    console.error("[Domivus ErrorBoundary]", error, info?.componentStack);
    this.setState({ info });
  }

  handleReload = () => {
    // State zurücksetzen und Seite neu laden
    this.setState({ error: null, info: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ error: null, info: null });
    window.location.href = "/";
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = String(this.state.error?.message || this.state.error || "Unbekannter Fehler");
    const stack = String(this.state.error?.stack || "");

    return (
      <div
        className="min-h-[60vh] flex items-center justify-center p-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-xl w-full bg-white border border-rose-200 rounded-lg shadow-sm p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 shrink-0 text-xl">
              !
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-slate-900">
                Diese Ansicht konnte nicht geladen werden.
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Es ist ein unerwarteter Fehler beim Rendern aufgetreten. Ihre Daten
                sind sicher – Sie können die Seite neu laden oder zur Übersicht
                zurückkehren.
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={this.handleReload}
                  className="text-sm px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-medium"
                  data-testid="error-boundary-reload"
                >
                  Seite neu laden
                </button>
                <button
                  onClick={this.handleHome}
                  className="text-sm px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 font-medium"
                  data-testid="error-boundary-home"
                >
                  Zur Startseite
                </button>
              </div>
              <details className="mt-4 text-xs text-slate-500">
                <summary className="cursor-pointer hover:text-slate-700 select-none">
                  Technische Details (für Support)
                </summary>
                <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200 font-mono whitespace-pre-wrap text-[10px] max-h-64 overflow-auto">
                  {msg}
                  {stack && "\n\n" + stack}
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
